import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Check, Heart, HeartOff, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Toast variants map to an icon and an accent, so a caller picks intent rather
 * than styling. "follow"/"unfollow" exist because following is the thing the
 * app toasts most, and the heart reads faster than a generic check.
 */
export type ToastVariant = "success" | "error" | "follow" | "unfollow";

export type ToastOptions = {
  variant?: ToastVariant;
  /** Secondary line, e.g. the venue or event name. */
  description?: string;
  /** Milliseconds before auto-dismiss. */
  duration?: number;
};

type Toast = ToastOptions & {
  id: number;
  message: string;
  /** Set while the exit animation plays, just before the toast is dropped. */
  leaving?: boolean;
};

type ToastContextValue = {
  toast: (message: string, options?: ToastOptions) => void;
};

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

const DEFAULT_DURATION = 3000;
/** Must match the exit animation duration below. */
const EXIT_MS = 150;

const VARIANT_ICON = {
  success: Check,
  error: AlertCircle,
  follow: Heart,
  unfollow: HeartOff,
} as const;

const VARIANT_ICON_CLASS = {
  success: "bg-primary/10 text-primary",
  error: "bg-destructive/10 text-destructive",
  follow: "bg-primary/10 text-primary",
  unfollow: "bg-muted text-muted-foreground",
} as const;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  // Tracked so we can clear every pending timer on unmount rather than letting
  // them fire against a gone component.
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Mark first so the exit animation can run, then drop the row.
      setToasts((current) =>
        current.map((t) => (t.id === id ? { ...t, leaving: true } : t))
      );
      later(
        () => setToasts((current) => current.filter((t) => t.id !== id)),
        EXIT_MS
      );
    },
    [later]
  );

  const toast = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const id = nextId.current++;
      // Cap the stack so a burst of taps cannot fill the screen; oldest go.
      setToasts((current) => [...current.slice(-2), { id, message, ...options }]);
      later(() => dismiss(id), options.duration ?? DEFAULT_DURATION);
    },
    [dismiss, later]
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* aria-live so the message reaches a screen reader without stealing focus. */}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
      >
        {toasts.map((t) => (
          <ToastRow key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastRow({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  const variant = toast.variant ?? "success";
  const Icon = VARIANT_ICON[variant];

  return (
    <button
      type="button"
      onClick={onDismiss}
      className={cn(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border border-border bg-popover p-3 text-left shadow-lg",
        "transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        toast.leaving
          ? "animate-out fade-out slide-out-to-bottom-2 duration-150"
          : "animate-in fade-in slide-in-from-bottom-2 duration-200"
      )}
    >
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full",
          VARIANT_ICON_CLASS[variant]
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 pt-0.5">
        <span className="block text-sm font-medium text-foreground">
          {toast.message}
        </span>
        {toast.description && (
          // truncate: names can be long, and the toast must not grow tall.
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {toast.description}
          </span>
        )}
      </span>
    </button>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
